import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { QUERY_STALE_TIME, isSupabaseConfigured } from '@/config';
import { listEventsForFixtures, listFixturesByRound } from '@/lib/data/provider';
import { useRealtimeInvalidation } from '@/lib/data/realtime';
import type { Tables } from '@/lib/supabase/types';
import { DEFAULT_SCORING_RULES, type Position, type ScoringRules } from '@scoring/rules.ts';

/**
 * Provisional live points, computed client-side from the synced event stream
 * (goals / assists / cards only - appearance, clean sheets and bonus need the
 * full post-match stats). Display-only and clearly labelled: the scoring
 * engine remains the single source of truth once the gameweek closes.
 */
export function useProvisionalGwPoints(options: {
  competitionId: string | undefined;
  season: number | undefined;
  roundLabel: string | undefined;
  playerPositions: Map<string, Position>;
  rules?: ScoringRules;
}) {
  const { competitionId, season, roundLabel } = options;
  const rules = options.rules ?? DEFAULT_SCORING_RULES;
  const enabled =
    isSupabaseConfigured && Boolean(competitionId) && season !== undefined && Boolean(roundLabel);

  const fixturesQuery = useQuery({
    queryKey: ['fantasy', 'round-fixtures', competitionId ?? 'none', season ?? 0, roundLabel ?? ''],
    queryFn: () =>
      listFixturesByRound(competitionId as string, season as number, roundLabel as string),
    staleTime: QUERY_STALE_TIME.live,
    enabled,
  });

  const startedFixtureIds = useMemo(
    () =>
      (fixturesQuery.data ?? [])
        .filter((fixture) => fixture.status === 'live' || fixture.status === 'finished')
        .map((fixture) => fixture.id),
    [fixturesQuery.data],
  );

  const eventsQuery = useQuery({
    queryKey: ['fantasy', 'round-events', ...startedFixtureIds],
    queryFn: () => listEventsForFixtures(startedFixtureIds),
    staleTime: QUERY_STALE_TIME.live,
    enabled: enabled && startedFixtureIds.length > 0,
  });

  useRealtimeInvalidation({
    channel: `provisional-${roundLabel ?? 'none'}`,
    table: 'match_events',
    invalidate: [['fantasy', 'round-events']],
    enabled: enabled && startedFixtureIds.length > 0,
  });

  const points = useMemo(() => {
    const map = new Map<string, number>();
    const add = (playerId: string | null, value: number) => {
      if (!playerId) return;
      map.set(playerId, (map.get(playerId) ?? 0) + value);
    };
    for (const event of (eventsQuery.data ?? []) as Tables<'match_events'>[]) {
      switch (event.type) {
        case 'goal':
        case 'penalty': {
          const position = event.player_id
            ? options.playerPositions.get(event.player_id)
            : undefined;
          add(event.player_id, position ? rules.goal[position] : rules.goal.FWD);
          add(event.assist_player_id, rules.assist);
          break;
        }
        case 'own_goal':
          add(event.player_id, rules.ownGoal);
          break;
        case 'yellow':
          add(event.player_id, rules.yellow);
          break;
        case 'red':
          add(event.player_id, rules.red);
          break;
        default:
          break;
      }
    }
    return map;
  }, [eventsQuery.data, options.playerPositions, rules]);

  return {
    points,
    anyLive: (fixturesQuery.data ?? []).some((fixture) => fixture.status === 'live'),
    fixtures: fixturesQuery.data ?? [],
  };
}
